import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Alert,
  Stack,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  Divider,
  Paper,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  CloudUpload,
  Email,
  Assessment,
  FolderOpen,
} from '@mui/icons-material';

import { API_BASE } from '../utils/apiUtils';

const KPIExtraction = () => {
  // Fetch Emails states
  const [fetchingEmails, setFetchingEmails] = useState(false);
  const [fetchResult, setFetchResult] = useState(null);

  // KPI Actions states
  const [actionsFile, setActionsFile] = useState(null);
  const [uploadingActions, setUploadingActions] = useState(false);
  const [actionsInfo, setActionsInfo] = useState(null);

  // Generate Report states
  const [selectedMonth, setSelectedMonth] = useState('');
  const [availableMonths, setAvailableMonths] = useState([]);
  const [generatingReport, setGeneratingReport] = useState(false);

  // Scan Folder state
  const [scanningFolder, setScanningFolder] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Fetch emails from Gmail (KPI data + Daily Job Plan for MOC compliance)
  const handleFetchEmails = async () => {
    setFetchingEmails(true);
    setFetchResult(null);

    try {
      // Fetches HSE KPI emails AND Daily Job Plan emails for MOC compliance checking (C63)
      const response = await fetch(`${API_BASE}/kpi-emails/fetch`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch emails');
      }

      const data = await response.json();
      setFetchResult(data);
      setSnackbar({
        open: true,
        message: data.message || 'Emails fetched successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error fetching emails:', error);
      setSnackbar({
        open: true,
        message: `Error fetching emails: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setFetchingEmails(false);
    }
  };

  // Upload KPI Actions Excel file
  const handleActionsUpload = async () => {
    if (!actionsFile) {
      setSnackbar({
        open: true,
        message: 'Please select an Excel file first',
        severity: 'warning',
      });
      return;
    }

    if (!selectedMonth) {
      setSnackbar({
        open: true,
        message: 'Please select a report month first',
        severity: 'warning',
      });
      return;
    }

    setUploadingActions(true);

    try {
      const formData = new FormData();
      formData.append('file', actionsFile);
      formData.append('reportMonth', selectedMonth);

      const response = await fetch(`${API_BASE}/kpi-emails/actions/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload KPI Actions file');
      }

      const data = await response.json();
      setActionsInfo(data);
      setSnackbar({
        open: true,
        message: data.message || 'KPI Actions processed successfully',
        severity: 'success',
      });
      setActionsFile(null); // Reset file input
    } catch (error) {
      console.error('Error uploading KPI Actions:', error);
      setSnackbar({
        open: true,
        message: `Error uploading KPI Actions: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setUploadingActions(false);
    }
  };

  // Download filtered Excel from output folder
  const handleDownloadReport = async () => {
    if (!selectedMonth) {
      setSnackbar({
        open: true,
        message: 'Please select a month first',
        severity: 'warning',
      });
      return;
    }

    setGeneratingReport(true);

    try {
      const response = await fetch(`${API_BASE}/kpi-emails/actions/download?reportMonth=${selectedMonth}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error('No filtered Excel file found for this month. Please process KPI Actions first.');
        }
        throw new Error('Failed to download filtered Excel');
      }

      // Download the Excel file
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const contentDisposition = response.headers.get('Content-Disposition');
      const filenameMatch = contentDisposition && contentDisposition.match(/filename="(.+)"/);
      const filename = filenameMatch ? filenameMatch[1] : `KPI_Actions_Filtered_${selectedMonth}.xlsx`;

      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setSnackbar({
        open: true,
        message: `Filtered Excel downloaded: ${filename}`,
        severity: 'success',
      });
    } catch (error) {
      console.error('Error downloading filtered Excel:', error);
      setSnackbar({
        open: true,
        message: `Error: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setGeneratingReport(false);
    }
  };

  // Handle KPI Actions file selection
  const handleActionsFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
        setSnackbar({
          open: true,
          message: 'Please select an Excel file (.xlsx or .xls)',
          severity: 'error',
        });
        return;
      }
      setActionsFile(file);
    }
  };

  // Scan landing folder
  const handleScanFolder = async () => {
    setScanningFolder(true);

    try {
      const response = await fetch(`${API_BASE}/kpi-emails/actions/scan-folder`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to scan folder');
      }

      const data = await response.json();

      // Extract reportMonth from response and set selectedMonth state
      if (data.reportMonth) {
        setSelectedMonth(data.reportMonth);
        console.log('Extracted report month from scanned file:', data.reportMonth);
      }

      setSnackbar({
        open: true,
        message: data.message || 'Folder scanned successfully',
        severity: 'success',
      });
    } catch (error) {
      console.error('Error scanning folder:', error);
      setSnackbar({
        open: true,
        message: `Error scanning folder: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setScanningFolder(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        KPI Extraction
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Extract KPI data from Gmail emails and generate Excel reports
      </Typography>

      {/* Step 1: Fetch Emails */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 1: Fetch Emails from Gmail
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Fetch HSE KPI emails from Gmail and extract data to the database.
          <br />This includes:
          <br />• HSE KPI data (incidents, safety metrics, etc.)
          <br />• Daily Job Plan (DJP) data for MOC Compliance KPI (C63: Unapproved MOC Execution)
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={fetchingEmails ? <CircularProgress size={20} color="inherit" /> : <Email />}
          onClick={handleFetchEmails}
          disabled={fetchingEmails}
          sx={{ mt: 2 }}
        >
          {fetchingEmails ? 'Fetching Emails...' : 'Fetch Emails from Gmail'}
        </Button>

        {fetchResult && (
          <Card sx={{ mt: 3, bgcolor: 'success.light' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Success!
              </Typography>
              <Typography variant="body2">
                Processed {fetchResult.emailsProcessed || 0} emails
              </Typography>
              <Typography variant="body2">
                Extracted {fetchResult.kpisExtracted || 0} KPI data points
              </Typography>
              {fetchResult.message && (
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  {fetchResult.message}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Step 2: Upload & Process KPI Actions */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 2: Process KPI Actions & MOC Compliance
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Upload KPI Actions Excel file or place it in the landing folder (D:/Chatbot/kpi-landing) and click "Scan Folder".
          <br />Filename must contain the report month (e.g., KPI_Actions_2025-10.xlsx).
          <br /><br /><strong>Automated Processing:</strong>
          <br />• Extracts Action Items (filters: Source, Status, Due Date)
          <br />• Extracts MOCs and PSSRs from Excel
          <br />• Calculates MOC Compliance KPIs:
          <br />  - C63: Unapproved MOC Execution (requires Daily Job Plan emails from Step 1)
          <br />  - C64: Closed MOCs without PSSR
          <br />• Saves all results to database automatically
        </Typography>

        <FormControl fullWidth sx={{ mt: 2, maxWidth: 300, mb: 2 }}>
          <TextField
            label="Report Month"
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            helperText="Select the month for KPI calculation (e.g., 2025-10)"
          />
        </FormControl>

        <Stack direction="row" spacing={2} alignItems="center" sx={{ mt: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<CloudUpload />}
          >
            Select KPI Actions Excel
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              onChange={handleActionsFileChange}
            />
          </Button>

          {actionsFile && (
            <Typography variant="body2">
              Selected: {actionsFile.name}
            </Typography>
          )}
        </Stack>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
          <Button
            variant="contained"
            color="primary"
            startIcon={uploadingActions ? <CircularProgress size={20} color="inherit" /> : <CloudUpload />}
            onClick={handleActionsUpload}
            disabled={!actionsFile || !selectedMonth || uploadingActions}
          >
            {uploadingActions ? 'Processing...' : 'Upload & Apply Filters'}
          </Button>

          <Button
            variant="outlined"
            color="secondary"
            startIcon={scanningFolder ? <CircularProgress size={20} color="inherit" /> : <FolderOpen />}
            onClick={handleScanFolder}
            disabled={scanningFolder}
          >
            {scanningFolder ? 'Scanning...' : 'Scan Folder'}
          </Button>
        </Stack>

        {actionsInfo && (
          <Card sx={{ mt: 3, bgcolor: 'info.light' }}>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Processing Complete
              </Typography>
              <Typography variant="body2">
                Total Actions: {actionsInfo.totalActions || 0}
              </Typography>
              <Typography variant="body2">
                Filtered Actions: {actionsInfo.filteredActions || 0}
              </Typography>
              <Typography variant="body2">
                Report Month: {actionsInfo.reportMonth}
              </Typography>
            </CardContent>
          </Card>
        )}
      </Paper>

      <Divider sx={{ my: 4 }} />

      {/* Step 3: Download Report */}
      <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 3: Download Filtered KPI Actions
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Download the filtered KPI Actions Excel file that was automatically saved to the output folder (D:/Chatbot/kpi-output)
        </Typography>

        <Button
          variant="contained"
          color="primary"
          startIcon={generatingReport ? <CircularProgress size={20} color="inherit" /> : <Assessment />}
          onClick={handleDownloadReport}
          disabled={!selectedMonth || generatingReport}
          sx={{ mt: 2 }}
        >
          {generatingReport ? 'Downloading...' : 'Download Filtered Excel'}
        </Button>
      </Paper>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default KPIExtraction;
